import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { CheckCircle, Package, Truck, Mail, ArrowRight, Clock, Heart } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useAppSelector } from '@/store/hooks';

interface OrderItem {
  id: string;
  name: string;
  price: number;
  quantity: number;
  image: string;
}

interface OrderDetails {
  orderId: string;
  orderNumber: string;
  items: OrderItem[];
  subtotal: number;
  shipping: number;
  tax: number;
  total: number;
  customerEmail: string;
  estimatedDelivery: string;
}

const ThankYou = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAppSelector((state) => state.auth);
  const [orderDetails, setOrderDetails] = useState<OrderDetails | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Get order details from location state or fetch from API
    const orderData = location.state?.orderDetails;
    
    if (orderData) {
      setOrderDetails(orderData);
    } else {
      // Mock data for demonstration - replace with actual API call
      setOrderDetails({
        orderId: "ORD-" + Date.now(),
        orderNumber: "#12345",
        items: [
          {
            id: "1",
            name: "B5 80mm Brush",
            price: 369.90,
            quantity: 10,
            image: "/placeholder.webp"
          },
          {
            id: "2", 
            name: "testing 123",
            price: 5800.00,
            quantity: 16,
            image: "/placeholder.webp"
          },
          {
            id: "3",
            name: "Gucci Dionysus Small Shoulder Bag",
            price: 250.00,
            quantity: 5,
            image: "/placeholder.webp"
          }
        ],
        subtotal: 1419.90,
        shipping: 5.99,
        tax: 113.59,
        total: 1539.48,
        customerEmail: user?.email || "customer@example.com",
        estimatedDelivery: "3-5 business days"
      });
    }
    
    setIsLoading(false);
  }, [location.state, user]);

  const handleContinueShopping = () => {
    navigate('/products');
  };

  const handleViewOrderHistory = () => {
    navigate('/order-history');
  };

  const handleGoHome = () => {
    navigate('/');
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-2xl mx-auto px-4 text-center">
        {/* Success Animation */}
        <div className="flex justify-center mb-8">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center animate-pulse">
            <CheckCircle className="w-10 h-10 text-green-600" />
          </div>
        </div>

        {/* Main Message */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Thank You for Your Order! 
          </h1>
          <p className="text-xl text-gray-600 mb-6">
            Your order has been successfully placed and is being processed.
          </p>
          <Badge variant="secondary" className="text-lg px-4 py-2">
            Order Confirmed ✓
          </Badge>
        </div>

        {/* Order Status Cards */}
        <div className="grid md:grid-cols-3 gap-4 mb-8">
          <Card className="border-2 border-green-200">
            <CardContent className="pt-6 text-center">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <CheckCircle className="w-6 h-6 text-green-600" />
              </div>
              <h3 className="font-semibold mb-2">Order Placed</h3>
              <p className="text-sm text-gray-600">Your order has been confirmed</p>
            </CardContent>
          </Card>

          <Card className="border-2 border-blue-200">
            <CardContent className="pt-6 text-center">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <Package className="w-6 h-6 text-blue-600" />
              </div>
              <h3 className="font-semibold mb-2">Processing</h3>
              <p className="text-sm text-gray-600">We're preparing your items</p>
            </CardContent>
          </Card>

          <Card className="border-2 border-yellow-200">
            <CardContent className="pt-6 text-center">
              <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <Truck className="w-6 h-6 text-yellow-600" />
              </div>
              <h3 className="font-semibold mb-2">On the Way</h3>
              <p className="text-sm text-gray-600">Ships within 2-3 days</p>
            </CardContent>
          </Card>
        </div>

        {/* What's Next */}
        <Card className="mb-8 text-left">
          <CardHeader>
            <CardTitle className="text-center">What Happens Next?</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <Mail className="w-4 h-4 text-blue-600" />
                </div>
                <div>
                  <h4 className="font-semibold">Confirmation Email</h4>
                  <p className="text-sm text-gray-600">
                    You'll receive an order confirmation email with all the details shortly.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-8 h-8 bg-yellow-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <Package className="w-4 h-4 text-yellow-600" />
                </div>
                <div>
                  <h4 className="font-semibold">Order Processing</h4>
                  <p className="text-sm text-gray-600">
                    Our team will carefully prepare and package your items.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <Truck className="w-4 h-4 text-green-600" />
                </div>
                <div>
                  <h4 className="font-semibold">Shipping & Tracking</h4>
                  <p className="text-sm text-gray-600">
                    You'll get tracking information once your order ships.
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Action Buttons */}
        <div className="space-y-4">
          <Button 
            onClick={handleViewOrderHistory}
            className="w-full bg-black hover:bg-gray-800 text-white py-3 text-lg"
          >
            View Order History
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
          
          <div className="grid grid-cols-2 gap-4">
            <Button 
              onClick={handleContinueShopping}
              variant="outline" 
              className="w-full py-3"
            >
              Continue Shopping
            </Button>
            
            <Button 
              onClick={handleGoHome}
              variant="outline"
              className="w-full py-3"
            >
              Go to Home
            </Button>
          </div>
        </div>

        {/* Delivery Estimate */}
        <Card className="mt-8 bg-gradient-to-r from-blue-50 to-purple-50 border-blue-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-center gap-2 mb-2">
              <Clock className="w-5 h-5 text-blue-600" />
              <h3 className="font-semibold text-blue-800">Estimated Delivery</h3>
            </div>
            <p className="text-2xl font-bold text-blue-800 mb-1">3-5 Business Days</p>
            <p className="text-sm text-blue-600">Standard shipping included</p>
          </CardContent>
        </Card>

        {/* Thank You Message */}
        <div className="mt-8 p-6 bg-white rounded-lg border-2 border-pink-200">
          <div className="flex items-center justify-center gap-2 mb-3">
            <Heart className="w-6 h-6 text-pink-500" />
            <h3 className="text-xl font-semibold text-gray-800">Thank You!</h3>
          </div>
          <p className="text-gray-600">
            We appreciate your business and hope you love your purchase. 
            If you have any questions, don't hesitate to contact our support team.
          </p>
        </div>

        {/* Footer */}
        <div className="mt-8 text-center">
          <p className="text-sm text-gray-500">
            Questions about your order?{' '}
            <button 
              className="underline hover:text-gray-700"
            //   onClick={() => navigate('/contact')}
            >
              Contact Support
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};

export default ThankYou;